import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Link as MuiLink,
  Divider,
  useTheme,
} from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import LoginIcon from '@mui/icons-material/Login';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';

import { signIn } from '../../../redux/auth/authSlice';
import { API_CALL_STATUS } from '../../../assets/constants/constants';
import Loader from '../../loader/Loader';
import { useNotification } from '../../shared/NotificationProvider';
import OAuth from '../shared/OAuth/OAuth';
import FormInput from '../shared/FormInput';

const SignInForm = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const apiCallStatus = useSelector(state => state.auth.authStatus);
  const notify = useNotification();

  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, formState: { isDirty, isValid, isSubmitting } } = methods;

  const onSubmit = async (data) => {
    const { email, password } = data;
    const res = await dispatch(signIn({ email, password }));

    if (res.payload?.status === 'success') {
      methods.reset();
      notify.success('Welcome back! Redirecting...');
      setTimeout(() => {
        navigate(location.state?.from?.pathname || "/fmr/posts");
      }, 1500);
    } else {
      notify.error(res.payload?.message || "Login failed. Please check your credentials.");
    }
  };

  return (
    <FormProvider {...methods}>
      {apiCallStatus === API_CALL_STATUS.LOADING && <Loader />}

      <Box 
        component="form" 
        onSubmit={handleSubmit(onSubmit)} 
        sx={{ 
          mt: 3,
          width: '100%',
        }}
      >
        <Stack spacing={3}>
          {/* Email Field with Animation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <FormInput
              name="email"
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              startIcon={<EmailIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
            />
          </motion.div>

          {/* Password Field with Animation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <FormInput
              name="password"
              label="Password"
              type="password"
              placeholder="••••••••"
              startIcon={<LockIcon sx={{ color: 'rgba(255,255,255,0.6)' }} />}
              showPasswordToggle={true}
            />
          </motion.div>

          {/* Forgot Password Link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <MuiLink
                onClick={() => navigate("/fmr/forgot-password")}
                underline="hover"
                sx={{
                  color: '#00ff88',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#66ffb3',
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot Password?
              </MuiLink>
            </Box>
          </motion.div>

          {/* Sign In Button with Animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              variant="gradient"
              type="submit"
              disabled={isSubmitting || apiCallStatus === 'loading'}
              fullWidth
              startIcon={<LoginIcon />}
              sx={{
                py: 1.8,
                fontSize: '1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '12px',
                background: 'linear-gradient(45deg, #00ff88, #0099ff)',
                color: '#000814',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent, rgba(255,255,255,0.2), transparent)',
                  transform: 'translateX(-100%)',
                  transition: 'transform 0.6s ease',
                },
                '&:hover::before': {
                  transform: 'translateX(100%)',
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 30px rgba(0, 255, 136, 0.4)',
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            >
              {isSubmitting || apiCallStatus === 'loading' ? 'Signing In...' : 'Sign In'}
            </Button>
          </motion.div>

          {/* Divider with Animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Divider 
              sx={{ 
                my: 1,
                '&::before, &::after': {
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Typography 
                variant="body2" 
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  px: 2,
                  background: 'linear-gradient(135deg, rgba(0, 29, 61, 0.8) 0%, rgba(0, 8, 20, 0.9) 100%)',
                }}
              >
                OR CONTINUE WITH
              </Typography>
            </Divider>
          </motion.div>

          {/* OAuth Section with Animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <OAuth />
          </motion.div>
        </Stack>
      </Box>

      {/* Additional Info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <Typography 
          variant="caption" 
          sx={{
            color: 'rgba(255, 255, 255, 0.5)',
            textAlign: 'center',
            mt: 4,
            display: 'block',
            fontSize: '0.75rem',
            lineHeight: 1.5,
          }}
        >
          By signing in, you agree to our Terms of Service and Privacy Policy.
        </Typography>
      </motion.div>
    </FormProvider>
  );
};

export default SignInForm;